/**
 * UnifiedPOITable - Unified POI table component
 *
 * A single shared component for displaying and editing POIs across:
 * - AssetsSearchPage (fullPage variant)
 * - AutodeskViewer sidebar (sidebar variant)
 * - WebIfcViewer sidebar (sidebar variant)
 * - SitesPage modal (modal variant)
 *
 * Uses POITableContext for shared state that persists across navigation.
 */

import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import AssetDialog from './AssetDialog';
import AssetConnections from './AssetConnections';
import AssetPanelResize from './AssetPanelResize';
import AssetFiltersDialog from './AssetFiltersDialog';
import { matchesAssetChoices, UNASSIGNED } from './assetFilters';
import { useSiteWriteAccess } from '../../hooks/useSiteWriteAccess';
import { useTheme } from '../../contexts/ThemeContext';
import { useTranslation } from '../../contexts/TranslationContext';
import { useRBAC } from '../../contexts/RBACContext';
import {
  usePOITable,
  matchesFilterGroups,
  getFieldValue,
  getPOIElementDbId,
  getPOISourceFile,
  isPOILoaded,
  isPOILoading,
  parseCustomData
} from '../../contexts/POITableContext';
import { apiService } from '../../services/ApiService';
import { useViewerPOIBatchActions } from '../shared/useViewerPOIBatchActions';
import { showErrorToast } from '../shared/toast';
import { logger } from '../../utils/log';
import { assignedRoomNames } from '../../utils/poiRowAggregation';
import { trackEvent } from '../../services/usageMetrics';
import { Bdi } from '../shared/BidiIsolate';
import { isNotAssetSelection } from '../../viewers/autodesk/sceneSelection';
import {
  POITableVariant,
  FilterCondition,
  FilterGroup,
  FILTER_FIELDS,
  OPERATORS,
  PAGE_SIZE,
  PAGE_SIZE_OPTIONS,
  type ViewerSelectedElement
} from '../../types/poi-table';

// ============================================================================
// Props Interface
// ============================================================================

interface UnifiedPOITableProps {
  variant: POITableVariant;
  siteId: string;

  // Viewer integration
  onNavigateTo3D?: (poi: any) => void;
  onHighlightGroup?: (pois: any[], color?: string) => void;
  onViewTickets?: (poi: any) => void;
  onCreateTicket?: (poi: any) => void;
  onPanelHeightChange?: (height: number) => void;
  onPOIClick?: (poi: any) => void;
  onBulkSelectionChange?: (dbIds: number[]) => void; // Notify viewer of bulk selection for protection tool

  // Feature flags
  showEditMode?: boolean;
  showBulkActions?: boolean;
  showColumnSelector?: boolean;
  showDeleteAll?: boolean;
  showExpandCollapsePanel?: boolean; // Show panel expand/collapse button (sidebar only)

  // Sidebar panel state
  onClose?: () => void; // Close button callback
  isPanelExpanded?: boolean; // Panel expand state (controlled)
  onPanelExpandedChange?: (expanded: boolean) => void; // Panel expand callback

  // Model state (for 3D navigation)
  loadedModels?: string[];
  loadingModels?: string[];
  onRequestLoadModel?: (fileName: string) => void;

  // Permissions (override from context)
  canEditContent?: boolean;

  // Custom render slots
  renderExtraActions?: (selectedPOIs: any[]) => React.ReactNode;
  renderExtraHeaderButtons?: () => React.ReactNode;

  // 3D viewer selection filter - can be dbIds (numbers) or objects with elementId/externalId/name/ifcGuid for matching
  viewerSelectedDbIds?: ViewerSelectedElement[];

  // Optional invalidation hook for embedded viewers that maintain their own POI caches
  onPOIsChanged?: () => void | Promise<void>;

  /**
   * "Create asset" for a selected element that is not an asset yet — the viewer's own action (the
   * Element panel's). Without it the "not an asset yet" line names the elements but offers no button.
   */
  onCreateAssetForSceneElement?: (element: { dbId: number; sourceFile?: string | null }) => Promise<any>;
  /** Changes when the host changed assets itself (e.g. made one): the table reloads its list. */
  reloadKey?: number;
}

// ============================================================================
// POI Attributes Panel (for expanded row)
// ============================================================================

interface POIAttributesPanelProps {
  poi: any;
  colors: any;
  dt?: boolean; // fullPage (DT design language) render — legacy path is unaffected when false/omitted
}

const POIAttributesPanel: React.FC<POIAttributesPanelProps> = ({ poi, colors, dt }) => {
  const customData = parseCustomData(poi);
  const rvtProps = customData?.rvt?.rvtProperties || {};
  const ifcProps = customData?.ifc?.properties || {};
  const ifcPropSets = customData?.ifc?.propertySets || {};

  const rvtPropCount = Object.keys(rvtProps).length;
  const ifcPropCount = Object.keys(ifcProps).length;
  const ifcPropSetCount = Object.keys(ifcPropSets).length;

  const sectionStyle: React.CSSProperties = { marginBottom: '16px' };
  const sectionHeaderStyle: React.CSSProperties = {
    fontSize: '13px',
    fontWeight: 600,
    color: colors.textPrimary,
    marginBottom: '8px',
    paddingBottom: '4px',
    borderBottom: `1px solid ${colors.border}`,
  };
  const propertyGridStyle: React.CSSProperties = {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
    gap: '4px 16px',
  };
  const propertyRowStyle: React.CSSProperties = { display: 'flex', fontSize: '12px', padding: '2px 0' };
  const propertyKeyStyle: React.CSSProperties = { fontWeight: 500, color: colors.textSecondary, minWidth: '120px', flexShrink: 0 };
  const propertyValueStyle: React.CSSProperties = { color: colors.textPrimary, wordBreak: 'break-word' as const };

  const renderPropertySection = (title: string, props: Record<string, any>, count: number) => {
    if (count === 0) return null;
    return (
      <div style={sectionStyle}>
        <div style={sectionHeaderStyle}>{title} ({count})</div>
        <div style={propertyGridStyle}>
          {Object.entries(props).map(([key, value]) => (
            <div key={key} style={propertyRowStyle}>
              <span style={propertyKeyStyle}>{key}:</span>
              <span style={propertyValueStyle}><Bdi>{String(value ?? '')}</Bdi></span>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderIfcPropertySets = () => {
    if (ifcPropSetCount === 0) return null;
    return Object.entries(ifcPropSets).map(([setName, setProps]) => {
      const propsObj = setProps as Record<string, any>;
      const propCount = Object.keys(propsObj).length;
      if (propCount === 0) return null;
      return (
        <div key={setName} style={sectionStyle}>
          <div style={sectionHeaderStyle}>{setName} ({propCount})</div>
          <div style={propertyGridStyle}>
            {Object.entries(propsObj).map(([key, value]) => (
              <div key={key} style={propertyRowStyle}>
                <span style={propertyKeyStyle}>{key}:</span>
                <span style={propertyValueStyle}><Bdi>{String(value ?? '')}</Bdi></span>
              </div>
            ))}
          </div>
        </div>
      );
    });
  };

  const basicInfo: Record<string, any> = {
    'ID': poi.id,
    'Name': poi.title || poi.name,
    'Category': getFieldValue(poi, 'category'),
    'Type': getFieldValue(poi, 'type'),
  };

  if (poi.coordinates) {
    const x = typeof poi.coordinates.x === 'number' ? poi.coordinates.x : parseFloat(poi.coordinates.x);
    const y = typeof poi.coordinates.y === 'number' ? poi.coordinates.y : parseFloat(poi.coordinates.y);
    const z = typeof poi.coordinates.z === 'number' ? poi.coordinates.z : parseFloat(poi.coordinates.z);
    if (!isNaN(x)) basicInfo['X'] = x.toFixed(2);
    if (!isNaN(y)) basicInfo['Y'] = y.toFixed(2);
    if (!isNaN(z)) basicInfo['Z'] = z.toFixed(2);
  }

  const hasAnyProperties = rvtPropCount > 0 || ifcPropCount > 0 || ifcPropSetCount > 0;

  // ── DT (fullPage) render — same extracted data, DT propgrid markup ──────────
  if (dt) {
    const dtGrid = (props: Record<string, any>) => (
      <div className="dt-propgrid">
        {Object.entries(props).map(([key, value]) => (
          <React.Fragment key={key}>
            <span className="k">{key}</span>
            <span className="v">{String(value ?? '')}</span>
          </React.Fragment>
        ))}
      </div>
    );
    return (
      <div className="dt-asset-properties">
        <div className="dt-propgrp" style={{ marginTop: 0 }}>Basic info</div>
        {dtGrid(basicInfo)}
        {rvtPropCount > 0 && (
          <>
            <div className="dt-propgrp">Revit properties ({rvtPropCount})</div>
            {dtGrid(rvtProps)}
          </>
        )}
        {ifcPropCount > 0 && (
          <>
            <div className="dt-propgrp">IFC properties ({ifcPropCount})</div>
            {dtGrid(ifcProps)}
          </>
        )}
        {ifcPropSetCount > 0 && Object.entries(ifcPropSets).map(([setName, setProps]) => {
          const propsObj = setProps as Record<string, any>;
          if (Object.keys(propsObj).length === 0) return null;
          return (
            <React.Fragment key={setName}>
              <div className="dt-propgrp">IFC property set · {setName}</div>
              {dtGrid(propsObj)}
            </React.Fragment>
          );
        })}
        {!hasAnyProperties && (
          <div className="dt-muted" style={{ fontStyle: 'italic' }}>
            No additional properties available.
          </div>
        )}
      </div>
    );
  }

  return (
    <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
      {renderPropertySection('Basic Info', basicInfo, Object.keys(basicInfo).length)}
      {renderPropertySection('Revit Properties', rvtProps, rvtPropCount)}
      {renderPropertySection('IFC Properties', ifcProps, ifcPropCount)}
      {renderIfcPropertySets()}
      {!hasAnyProperties && (
        <div style={{ color: colors.textSecondary, fontSize: '12px', fontStyle: 'italic' }}>
          No additional properties available.
        </div>
      )}
    </div>
  );
};

// ============================================================================
// DT (fullPage) presentation helpers
// ============================================================================

// Page-header glyph (mirrors the mockup's Assets icon; stroked by .dt-phead .pic svg)
const ASSET_ICON = (
  <svg viewBox="0 0 24 24">
    <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
    <path d="M3.27 6.96 12 12.01l8.73-5.05" />
    <path d="M12 22.08V12" />
  </svg>
);

// System chip dot color — self-contained token pick by discipline keyword (presentation only)
const systemChipColor = (val: string): string => {
  const v = (val || '').toLowerCase();
  if (/(elec|power|light)/.test(v)) return 'var(--dt-warn)';
  if (/(fire|sprinkler|safety|alarm)/.test(v)) return 'var(--dt-bad)';
  if (/(plumb|water|drain|sanitary)/.test(v)) return 'var(--dt-good)';
  return 'var(--dt-info)';
};

/** Elements the "not an asset yet" line shows before "+N more" (a window selection can hold hundreds). */
const SCENE_LINE_MAX = 4;

// ============================================================================
// Main Component
// ============================================================================

const UnifiedPOITable: React.FC<UnifiedPOITableProps> = ({
  variant,
  siteId,
  onNavigateTo3D,
  onHighlightGroup,
  onPOIClick, onViewTickets, onCreateTicket, onPanelHeightChange,
  onBulkSelectionChange,
  showEditMode = true,
  showBulkActions = true,
  showColumnSelector = true,
  showExpandCollapsePanel = false,
  onClose,
  isPanelExpanded,
  onPanelExpandedChange,
  loadedModels = [],
  loadingModels = [],
  onRequestLoadModel,
  canEditContent: canEditContentProp,
  renderExtraActions,
  renderExtraHeaderButtons,
  viewerSelectedDbIds = [],
  onPOIsChanged,
  onCreateAssetForSceneElement,
  reloadKey,
}) => {
  const { colors } = useTheme();
  const { t, isRTL } = useTranslation();
  const { isAdmin, hasRole } = useRBAC();

  // Context state
  const {
    setSiteId,
    pois,
    loading,
    error,
    refreshPOIs,
    searchTerm,
    setSearchTerm,
    filterGroups: appliedFilterGroups,
    setFilterGroups: setAppliedFilterGroups,
    assetChoices, setAssetChoices, facetReader, searchedPOIs,
    setShowFilterPanel,
    columns,
    setColumns,
    showColumnSelector: showColSelector,
    setShowColumnSelector: setShowColSelector,
    sortConfig,
    currentPage,
    setCurrentPage,
    pageSize,
    setPageSize,
    selectedPOIs,
    setSelectedPOIs,
    expandedPOI,
    setExpandedPOI,
    assignmentStats,
    isEditMode,
    setIsEditMode,
    editedPOIs,
    setEditedPOIs,
    modifiedPOIs,
    setModifiedPOIs,
    savingPOIs,
    setSavingPOIs,
    availableRooms,
    fieldOptions,
    dynamicColumns,
    allAvailableColumns,
    handleSort,
    handleSelectAll,
    handleSelectPOI,
  } = usePOITable();

  const [filterDialogOpen, setFilterDialogOpen] = useState(false);
  const [filterGroupDraft, setFilterGroupDraft] = useState<FilterGroup[]>([]);
  const filterGroups = filterDialogOpen ? filterGroupDraft : appliedFilterGroups;
  const setFilterGroups = filterDialogOpen ? setFilterGroupDraft : setAppliedFilterGroups;
  const openFilters = () => { setFilterGroupDraft(appliedFilterGroups.map(group => ({ ...group, conditions: group.conditions.map(condition => ({ ...condition })) }))); setFilterDialogOpen(true); };
  const [followScene, setFollowScene] = useState(true);
  // Set site ID in context when it changes
  useEffect(() => {
    setSiteId(siteId);
  }, [siteId, setSiteId]);

  // Permissions
  // ONE write rule, the server's own (permission_level). This asked
  // `isAdmin || hasRole('data_creator', …)`, which hid this work from site admins although
  // every route behind it admits them — and, since migration 069, from an onboarding
  // assignee who is supposed to be filling the site in.
  const canWriteSite = useSiteWriteAccess(siteId);
  const canEditContent = canEditContentProp ?? canWriteSite;

  // Debounced search: local state for immediate input display, delayed context update
  const [localSearchInput, setLocalSearchInput] = useState(searchTerm);
  const searchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => () => { if (searchTimerRef.current) clearTimeout(searchTimerRef.current); }, []);
  // Sync local input when context searchTerm changes externally (e.g. cleared by another component)
  useEffect(() => {
    setLocalSearchInput(searchTerm);
  }, [searchTerm]);

  // Filter mode: 'all' shows all POIs, 'selected' shows only POIs matching 3D viewer selection
  const [filterMode, setFilterMode] = useState<'all' | 'selected'>('all');

  // The 3D selection, split (viewers/autodesk/sceneSelection.ts): the ASSETS the table shrinks to,
  // and the selected elements that are NOT assets yet — named on their own line above the list,
  // never filtered by (filtering by them is what emptied the table: audit 2026-10-05, bug 6).
  const sceneAssetSelection = useMemo(() => viewerSelectedDbIds.filter(sel => !isNotAssetSelection(sel)), [viewerSelectedDbIds]);
  const sceneNonAssets = useMemo(
    () => viewerSelectedDbIds.filter(isNotAssetSelection) as Array<Exclude<ViewerSelectedElement, number>>,
    [viewerSelectedDbIds]
  );

  // The "not an asset yet" line: a long selection shows its first few until asked for the rest.
  const [sceneLineExpanded, setSceneLineExpanded] = useState(false);
  const [creatingSceneKey, setCreatingSceneKey] = useState<string | null>(null);
  const canCreateSceneAsset = canWriteSite && !!onCreateAssetForSceneElement;
  useEffect(() => { if (sceneNonAssets.length <= SCENE_LINE_MAX) setSceneLineExpanded(false); }, [sceneNonAssets.length]);

  // The host changed assets itself (the viewer made or removed one): reload the list quietly, so a
  // new asset is here for the table to shrink to. Until it arrives, an empty result says "loading",
  // not "No assets match".
  const [hostReloading, setHostReloading] = useState(false);
  const reloadKeyRef = useRef(reloadKey);
  useEffect(() => {
    if (reloadKey === undefined || reloadKey === reloadKeyRef.current) return;
    reloadKeyRef.current = reloadKey;
    let current = true;
    setHostReloading(true);
    refreshPOIs({ quiet: true }).finally(() => { if (current) setHostReloading(false); });
    return () => { current = false; };
  }, [reloadKey, refreshPOIs]);

  // Auto-switch to 'selected' mode when assets are selected in the 3D viewer; elements that are
  // not assets leave the list as it is.
  useEffect(() => {
    if (!followScene) return;
    if (sceneAssetSelection.length > 0) {
      logger.debug('[UnifiedPOITable] Switching to selected mode');
      setFilterMode('selected');
    } else {
      logger.debug('[UnifiedPOITable] Switching to all mode');
      setFilterMode('all');
    }
  }, [sceneAssetSelection, followScene]);

  // Filter POIs based on viewer selection when in 'selected' mode
  const sceneScopedPOIs = useMemo(() => {
    if (filterMode !== 'selected') return searchedPOIs;
    if (!sceneAssetSelection.length) return [];

    // Extract all possible IDs from selection for matching (using Sets for O(1) lookups)
    const selectedDbIds = new Set<number>();
    const selectedModelKeys = new Set<string>(); // "sourcefile:dbid" — model-scoped identity for multi-model sites
    const selectedModelKeyDbIds = new Set<number>(); // bare dbIds of the tagged selections, for source-file-less POIs
    const selectedElementIds = new Set<number>();
    const selectedExternalIds: string[] = [];
    const selectedNames: string[] = [];
    const selectedIfcGuids = new Set<string>();

    for (const sel of sceneAssetSelection) {
      if (typeof sel === 'number') {
        selectedDbIds.add(sel);
      } else if (sel && typeof sel === 'object') {
        if (sel.dbId) {
          // A source-file-tagged selection is model-scoped (the viewer reverse-lookup on
          // multi-model sites); a bare dbId (bulk/IFC/legacy emitters) stays model-agnostic.
          if (sel.sourceFile) {
            selectedModelKeys.add(`${String(sel.sourceFile).toLowerCase()}:${sel.dbId}`);
            selectedModelKeyDbIds.add(sel.dbId);
          } else {
            selectedDbIds.add(sel.dbId);
          }
        }
        if (sel.elementId) selectedElementIds.add(sel.elementId);
        if (sel.externalId) selectedExternalIds.push(sel.externalId);
        if (sel.name) selectedNames.push(sel.name);
        if (sel.ifcGuid) selectedIfcGuids.add(sel.ifcGuid);
      }
    }

    // Filter to POIs matching elementId (primary), dbId, externalId/GUID, or name
    const result = searchedPOIs.filter(poi => {
      const customData = parseCustomData(poi);
      const poiRvtDbId = getPOIElementDbId(poi);

      // Check ElementId match (Revit ElementId should match POI's rvtElementDbId)
      if (poiRvtDbId && selectedElementIds.size > 0) {
        if (selectedElementIds.has(poiRvtDbId)) {
          return true;
        }
      }

      // Model-scoped dbId match: the viewer reverse-lookup tags each selection with its source
      // file, so overlapping dbIds across federated models cannot cross-match the wrong POI.
      if (poiRvtDbId && selectedModelKeys.size > 0) {
        const poiSourceFile = getPOISourceFile(poi);
        if (poiSourceFile) {
          if (selectedModelKeys.has(`${poiSourceFile.toLowerCase()}:${poiRvtDbId}`)) {
            return true;
          }
        } else if (selectedModelKeyDbIds.has(poiRvtDbId)) {
          // POI carries no source file -> cannot be model-scoped; fall back to a bare dbId match
          // (restores the prior selection for such POIs without cross-matching source-tagged ones).
          return true;
        }
      }

      // Bare dbId match (bulk selection from table, IFC viewer, legacy emitters — no source file)
      if (poiRvtDbId && selectedDbIds.has(poiRvtDbId)) {
        return true;
      }

      // Check externalId/GUID match (partial match needed, keep as array)
      const poiGuid = customData?.rvt?.rvtElementGuid;
      if (poiGuid && selectedExternalIds.length > 0) {
        for (const extId of selectedExternalIds) {
          if (extId === poiGuid || extId.includes(poiGuid) || poiGuid.includes(extId)) {
            return true;
          }
        }
      }

      // Check IfcGUID match
      if (selectedIfcGuids.size > 0) {
        const poiIfcGuid = customData?.ifc?.guid || poi.ifc_guid;
        if (poiIfcGuid && selectedIfcGuids.has(poiIfcGuid)) {
          return true;
        }
      }

      // Check name match (last resort, partial match needed)
      if (selectedNames.length > 0) {
        const poiName = poi.name || customData?.rvt?.name;
        if (poiName) {
          for (const selName of selectedNames) {
            if (poiName === selName || poiName.includes(selName) || selName.includes(poiName)) {
              return true;
            }
          }
        }
      }

      return false;
    });

    logger.debug('[UnifiedPOITable] Matching POIs found:', result.length);
    return result;
  }, [searchedPOIs, filterMode, sceneAssetSelection]);

  const displayedPOIs = useMemo(() => sceneScopedPOIs.filter(poi => matchesFilterGroups(poi, appliedFilterGroups) && matchesAssetChoices(poi, assetChoices, facetReader)), [sceneScopedPOIs, appliedFilterGroups, assetChoices, facetReader]);
  const localTotalPages = Math.max(1, Math.ceil(displayedPOIs.length / pageSize));
  const displayPage = Math.min(currentPage, localTotalPages);
  const localPaginatedPOIs = useMemo(() => displayedPOIs.slice((displayPage - 1) * pageSize, displayPage * pageSize), [displayedPOIs, displayPage, pageSize]);
  const selectedOutsideResults = selectedPOIs.size - displayedPOIs.filter(poi => selectedPOIs.has(String(poi.id))).length;

  // Local state for modals
  const [showDeleteAllModal, setShowDeleteAllModal] = useState(false);
  /** The row last clicked — the one the model is showing. Not the bulk (checkbox) selection. */
  const [activePOI, setActivePOI] = useState<string | null>(null);
  const [roomPickerOpenForPOI, setRoomPickerOpenForPOI] = useState<number | null>(null);

  // Set Systems / Set Locations batch actions — the shared hook (D-9 adopts the 3.2
  // follow-up documented in REFACTORING-LOG). Transport adapter preserves this table's
  // exact drift: raw fetch + getAuthHeaders on siteId; SILENT no-op on non-ok (open-load
  // leaves state, apply skips); catch → result{failed: selected count}; reload awaited via
  // handlePOIMutationSuccess. This table's SIMPLER modal JSX stays its own (documented
  // DOM NO-GO) and reads the hook's state atoms below.
  const poiBatch = useViewerPOIBatchActions({
    getAssetId: () => siteId,
    selectedPOIs,
    reloadPOIs: () => handlePOIMutationSuccess(),
    awaitReload: true,
    minSubcategoryLevels: 0,
    transport: {
      loadSystemsProperties: async (assetId) => {
        // Silent-null contract on failure (this surface leaves the modal state as-is)
        const data = await apiService.get(`/assets/${assetId}/pois/available-properties`)
          .catch((error) => { console.error('Failed to load available properties:', error); return null; });
        if (!data) return null;
        return { properties: data.properties || [], propertyValues: data.propertyValues || {} };
      },
      loadLocationsProperties: async (assetId) => {
        const data = await apiService.get(`/assets/${assetId}/pois/available-properties`)
          .catch((error) => { console.error('Failed to load available properties:', error); return null; });
        if (!data) return null;
        return { properties: data.properties || [] };
      },
      applySystems: async (assetId, poiIds, systemProperty, subcategoryLevels) => {
        const data = await apiService.post(`/assets/${assetId}/pois/reassign-systems-batch`,
          { poiIds, systemProperty, subcategoryLevels }).catch(() => null);
        if (!data) return null;
        return { updated: data.updated || 0, unchanged: data.unchanged || 0, failed: data.failed || 0 };
      },
      applyLocations: async (assetId, poiIds, storeyProperty, spaceProperty) => {
        const data = await apiService.post(`/assets/${assetId}/pois/reassign-locations-batch`, {
          poiIds,
          storeyProperty: storeyProperty || null,
          spaceProperty: spaceProperty || null
        }).catch(() => null);
        if (!data) return null;
        return { updated: data.updated || 0, unchanged: data.unchanged || 0, failed: data.failed || 0 };
      },
      onApplySystemsError: (error, selectedCount) => {
        console.error('Failed to apply systems batch:', error);
        return { updated: 0, unchanged: 0, failed: selectedCount };
      },
      onApplyLocationsError: (error, selectedCount) => {
        console.error('Failed to apply locations batch:', error);
        return { updated: 0, unchanged: 0, failed: selectedCount };
      },
    },
  });

  const {
    showSetSystemsModal, setShowSetSystemsModal,
    systemsAvailableProperties, systemsPropertyValues,
    selectedSystemProperty, setSelectedSystemProperty,
    systemsSubcategoryLevels, setSystemsSubcategoryLevels,
    systemsSubcategorySearches, setSystemsSubcategorySearches,
    systemPropertySearch, setSystemPropertySearch,
    isLoadingSystemsProperties, isApplyingSystems,
    systemsResult, setSystemsResult,
    showSetLocationsModal, setShowSetLocationsModal,
    locationsAvailableProperties,
    selectedStoreyProperty, setSelectedStoreyProperty,
    selectedSpaceProperty, setSelectedSpaceProperty,
    storeyPropertySearch, setStoreyPropertySearch,
    spacePropertySearch, setSpacePropertySearch,
    isLoadingLocationsProperties, isApplyingLocations,
    locationsResult, setLocationsResult,
    handleOpenSetSystemsModal, handleApplySystemsBatch,
    handleOpenSetLocationsModal, handleApplyLocationsBatch,
  } = poiBatch;

  // Column selector search state
  const [columnSearch, setColumnSearch] = useState('');

  // Filter field dropdown state (for searchable dropdown)
  const [openFilterDropdown, setOpenFilterDropdown] = useState<string | null>(null);
  const [filterFieldSearch, setFilterFieldSearch] = useState('');

  // Delete all state
  const [isDeletingAll, setIsDeletingAll] = useState(false);

  // Delete selected state
  const [showDeleteSelectedModal, setShowDeleteSelectedModal] = useState(false);
  const [isDeletingSelected, setIsDeletingSelected] = useState(false);

  // Column resize - DOM-based approach for performance (no React re-renders during drag)
  const resizeRef = useRef<{
    column: string | null;
    startX: number;
    startWidth: number;
    currentWidth: number;
    thElement: HTMLElement | null;
    tableElement: HTMLElement | null;
  }>({
    column: null,
    startX: 0,
    startWidth: 0,
    currentWidth: 0,
    thElement: null,
    tableElement: null,
  });
  const tableRef = useRef<HTMLTableElement>(null);
  const wasResizingRef = useRef(false);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!resizeRef.current.column) return;
      const delta = (e.clientX - resizeRef.current.startX) * (isRTL ? -1 : 1);
      const newWidth = Math.max(50, resizeRef.current.startWidth + delta);
      resizeRef.current.currentWidth = newWidth;

      // Direct DOM mutation — no React re-render during drag
      const th = resizeRef.current.thElement;
      if (th) {
        th.style.width = `${newWidth}px`;
      }

      // Update table total width
      const table = resizeRef.current.tableElement;
      if (table) {
        const allThs = table.querySelectorAll('thead th');
        let total = 0;
        allThs.forEach(el => {
          total += (el as HTMLElement).offsetWidth;
        });
        table.style.width = `${total}px`;
      }
    };

    const handleMouseUp = () => {
      if (!resizeRef.current.column) return;

      // Commit the exact drag width to React state (not offsetWidth which includes padding)
      const finalWidth = resizeRef.current.currentWidth;
      const field = resizeRef.current.column;
      if (field && finalWidth > 0) {
        setColumns(prev => prev.map(c =>
          c.field === field ? { ...c, width: finalWidth } : c
        ));
      }

      resizeRef.current.column = null;
      resizeRef.current.thElement = null;
      resizeRef.current.tableElement = null;
      document.body.style.cursor = '';
      document.body.style.userSelect = '';

      // Clear resize flag after a tick so click handler can check it
      setTimeout(() => { wasResizingRef.current = false; }, 0);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [setColumns, isRTL]);

  const handleResizeStart = (e: React.MouseEvent, field: string, currentWidth: number) => {
    e.preventDefault();
    e.stopPropagation();
    wasResizingRef.current = true;
    const thElement = (e.currentTarget as HTMLElement).parentElement as HTMLElement;
    resizeRef.current = {
      column: field,
      startX: e.clientX,
      startWidth: currentWidth,
      currentWidth,
      thElement,
      tableElement: tableRef.current,
    };
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  };

  // ============================================================================
  // Style Objects
  // ============================================================================

  const isSidebar = variant === 'sidebar';

  // ============================================================================
  // Filter Group Handlers
  // ============================================================================

  const addFilterGroup = () => {
    setFilterGroups([...filterGroups, {
      id: `group_${Date.now()}`,
      logic: 'AND',
      conditions: []
    }]);
    setShowFilterPanel(true);
  };

  const removeFilterGroup = (groupId: string) => {
    setFilterGroups(filterGroups.filter(g => g.id !== groupId));
  };

  const addCondition = (groupId: string) => {
    setFilterGroups(filterGroups.map(g => {
      if (g.id === groupId) {
        return {
          ...g,
          conditions: [...g.conditions, {
            id: `cond_${Date.now()}`,
            field: 'name',
            operator: 'contains' as const,
            value: ''
          }]
        };
      }
      return g;
    }));
  };

  const removeCondition = (groupId: string, conditionId: string) => {
    setFilterGroups(filterGroups.map(g => {
      if (g.id === groupId) {
        return { ...g, conditions: g.conditions.filter(c => c.id !== conditionId) };
      }
      return g;
    }));
  };

  const updateCondition = (groupId: string, conditionId: string, updates: Partial<FilterCondition>) => {
    setFilterGroups(filterGroups.map(g => {
      if (g.id === groupId) {
        return {
          ...g,
          conditions: g.conditions.map(c => c.id === conditionId ? { ...c, ...updates } : c)
        };
      }
      return g;
    }));
  };

  const updateGroupLogic = (groupId: string, logic: 'AND' | 'OR') => {
    setFilterGroups(filterGroups.map(g => g.id === groupId ? { ...g, logic } : g));
  };

  const clearFilters = () => {
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    setFilterGroups([]);
    setLocalSearchInput('');
    setSearchTerm('');
    setCurrentPage(1);
  };

  // ============================================================================
  // Edit Mode Handlers
  // ============================================================================

  // (a dead handleEnterEditMode initializer that pre-populated initialEdits lived here —
  // zero callers; the Edit button enters lazily via setIsEditMode(true))

  const handleCancelEditMode = () => {
    setIsEditMode(false);
    setEditedPOIs({});
    setModifiedPOIs(new Set());
    setRoomPickerOpenForPOI(null);
  };

  const handleFieldChange = (poiId: string, field: string, value: string, originalValue: string) => {
    setEditedPOIs(prev => ({
      ...prev,
      [poiId]: { ...prev[poiId], [field]: value }
    }));

    const poi = pois.find(p => p.id === poiId || String(p.id) === poiId);
    if (poi) {
      const poiIdNum = typeof poi.id === 'number' ? poi.id : parseInt(poi.id, 10);
      if (value !== originalValue) {
        setModifiedPOIs(prev => new Set([...prev, poiIdNum]));
      }
    }
  };

  const handleRoomToggle = (poiId: number, roomName: string) => {
    const poiIdStr = String(poiId);
    const currentEdits = editedPOIs[poiIdStr] || {};
    // Seed from the POI's real Spaces assignments, never from the displayed room string —
    // the display falls back to metadata room names that have no matching Space row, so
    // splitting it round-trips phantom assignments the save silently drops.
    const poi = pois.find(p => String(p.id) === poiIdStr);
    const currentRooms: string[] = currentEdits.room_name_array || assignedRoomNames(poi);

    let newRooms: string[];
    if (currentRooms.includes(roomName)) {
      newRooms = currentRooms.filter((r: string) => r !== roomName);
    } else {
      newRooms = [...currentRooms, roomName];
    }

    setEditedPOIs(prev => ({
      ...prev,
      [poiIdStr]: {
        ...prev[poiIdStr],
        room_name_array: newRooms,
        room_name: newRooms.join(', ')
      }
    }));

    setModifiedPOIs(prev => new Set([...prev, poiId]));
  };

  const notifyPOIsChanged = useCallback(async () => {
    if (!onPOIsChanged) return;

    try {
      await onPOIsChanged();
    } catch (error) {
      console.error('[UnifiedPOITable] Failed to notify embedded viewer POI refresh:', error);
    }
  }, [onPOIsChanged]);

  const handlePOIMutationSuccess = useCallback(async () => {
    await refreshPOIs();
    await notifyPOIsChanged();
  }, [refreshPOIs, notifyPOIsChanged]);

  const handleSaveAllEdits = async () => {
    if (!siteId || modifiedPOIs.size === 0) return;

    setSavingPOIs(true);
    try {
      const poisToUpdate = Array.from(modifiedPOIs).map(poiId => {
        const poiIdStr = String(poiId);
        const edits = editedPOIs[poiIdStr];
        if (!edits) return null;
        const payload: Record<string, any> = { id: poiId };
        if (edits.name !== undefined) { payload.title = edits.name; payload.name = edits.name; }
        if (edits.subcategory !== undefined) payload.subcategory = edits.subcategory;
        if (edits.room_name !== undefined) { payload.room_name = edits.room_name; payload.room_name_array = edits.room_name_array; }
        if (edits.room_number !== undefined) payload.room_number = edits.room_number;
        if (edits.level !== undefined) payload.level = edits.level;
        if (edits.workset !== undefined) payload.workset = edits.workset;
        if (edits.phase !== undefined) payload.phase = edits.phase;
        if (edits.mark !== undefined) payload.mark = edits.mark;
        if (edits.system_category !== undefined) payload.system_category = edits.system_category;
        return payload;
      }).filter(Boolean);

      try {
        const result = await apiService.put(`/assets/${siteId}/pois/batch-update`, { pois: poisToUpdate });
        if (result.failedCount > 0) throw new Error(`${result.failedCount} assets could not be saved`);
        await handlePOIMutationSuccess();
        handleCancelEditMode();
      } catch (saveError) {
        console.error('Failed to save edits:', saveError);
        showErrorToast('Failed to save changes. Please try again.');
      }
    } catch (error) {
      console.error('Error saving edits:', error);
      showErrorToast('Error saving changes. Please try again.');
    } finally {
      setSavingPOIs(false);
    }
  };

  // ============================================================================
  // Show in 3D
  // ============================================================================

  const showSelectedIn3D = () => {
    if (selectedPOIs.size === 0) return;
    const selectedPOIsList = pois.filter(p => selectedPOIs.has(String(p.id)));

    // Standard highlight via onHighlightGroup
    if (onHighlightGroup) {
      onHighlightGroup(selectedPOIsList, '#48CAE4');
    }

    // Notify viewer for bulk protection tool (AutodeskViewer uses this)
    if (onBulkSelectionChange) {
      const dbIds = selectedPOIsList.map(poi => {
        return getPOIElementDbId(poi);
      }).filter((id): id is number => typeof id === 'number' && !isNaN(id));
      logger.debug('[UnifiedPOITable] showSelectedIn3D - dbIds:', dbIds.length, 'elements', dbIds.slice(0, 5));
      onBulkSelectionChange(dbIds);
    }
  };

  // ============================================================================
  // Delete All Handler (Admin Only)
  // ============================================================================

  const handleDeleteAll = async () => {
    if (!siteId || !isAdmin) return;

    setIsDeletingAll(true);
    try {
      try {
        await apiService.delete(`/assets/${siteId}/pois`);
        await handlePOIMutationSuccess();
        setShowDeleteAllModal(false);
      } catch {
        showErrorToast('Failed to delete all POIs');
      }
    } catch (error) {
      console.error('Error deleting POIs:', error);
      showErrorToast('Error deleting POIs');
    } finally {
      setIsDeletingAll(false);
    }
  };

  // ============================================================================
  // Delete Selected Handler (Content Creator + Admin)
  // ============================================================================

  const handleDeleteSelected = async () => {
    if (!siteId || !canEditContent || selectedPOIs.size === 0) return;

    setIsDeletingSelected(true);
    try {
      const poiIds = Array.from(selectedPOIs);
      try {
        await apiService.delete(`/assets/${siteId}/pois/batch-delete`, { poiIds });
        await handlePOIMutationSuccess();
        setSelectedPOIs(new Set()); // Clear selection
        setShowDeleteSelectedModal(false);
      } catch (deleteError: any) {
        showErrorToast(deleteError?.message || 'Failed to delete selected assets');
      }
    } catch (error) {
      console.error('Error deleting selected POIs:', error);
      showErrorToast('Error deleting selected assets');
    } finally {
      setIsDeletingSelected(false);
    }
  };

  // ============================================================================
  // Export CSV Handler
  // ============================================================================

  const handleExportCSV = (scope: 'matching' | 'selected' = 'matching') => {
    const visibleCols = columns.filter(c => c.visible);
    const headers = visibleCols.map(c => c.label);

    const escapeCSV = (val: unknown) => {
      const text = val == null ? '' : String(val);
      if (text.includes(',') || text.includes('"') || text.includes('\n') || text.includes('\r')) {
        return `"${text.replace(/"/g, '""')}"`;
      }
      return text;
    };

    const rows = (scope === 'selected' ? pois.filter(poi => selectedPOIs.has(String(poi.id))) : displayedPOIs).map(poi =>
      visibleCols.map(col => escapeCSV(getFieldValue(poi, col.field)))
    );

    const csv = [headers.map(escapeCSV).join(','), ...rows.map(r => r.join(','))].join('\n');
    const blob = new Blob([`\uFEFF${csv}`], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `assets-export-${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  // ============================================================================
  // Set Systems Modal Handlers
  // ============================================================================

  // Set Systems / Set Locations open+apply handlers come from useViewerPOIBatchActions
  // (destructured above); the transport adapter preserves this table's exact behavior.

  // ============================================================================
  // Visible columns for table rendering
  // ============================================================================

  const visibleColumns = useMemo(() => columns.filter(c => c.visible), [columns]);

  const CHECKBOX_COL_WIDTH = 60;
  const ACTIONS_COL_WIDTH = 100;
  const totalTableWidth = useMemo(() => {
    return visibleColumns.reduce((sum, col) => sum + col.width, 0) + CHECKBOX_COL_WIDTH + ACTIONS_COL_WIDTH;
  }, [visibleColumns]);

  // ============================================================================
  // Loading State
  // ============================================================================

  if (loading || error) return <div className="dt-asset-workspace dt-statebox" dir={isRTL ? 'rtl' : 'ltr'}>{error || t('loading', 'Loading assets...')}</div>;

  {
    // Active filter conditions (flattened) — presentation of existing filterGroups state
    const activeConditions = appliedFilterGroups.flatMap(g => g.conditions.map(c => ({ groupId: g.id, cond: c })));
    const allFilterFields = [
      ...FILTER_FIELDS.map(f => ({ field: f.field, label: f.label })),
      ...dynamicColumns.map(c => ({ field: c.field, label: c.label })),
    ];
    const fieldLabelFor = (field: string) => allFilterFields.find(f => f.field === field)?.label || field;
    const operatorLabelFor = (op: string) => OPERATORS.find(o => o.value === op)?.label || op;

    const roomEditableFields = ['name', 'subcategory', 'room_number', 'level', 'workset', 'phase', 'mark', 'system_category'];

    return (
      <div className={`dt-asset-workspace ${isSidebar ? 'dt-asset-embedded' : 'dt-asset-page'}`} dir={isRTL ? 'rtl' : 'ltr'}>
        {isSidebar && onPanelHeightChange && <AssetPanelResize onResize={onPanelHeightChange} />}
        {/* ── Page header ─────────────────────────────────────────────────── */}
        <div className="dt-asset-header">
          <div className="pic">{ASSET_ICON}</div>
          <div>
            <h2>{t('poiAssetsTitle', 'Assets')}</h2>
            <div className="sub">{pois.length} {t('poiElements', 'elements')}</div>
          </div>
          <div className="pa" style={{ position: 'relative' }}>
            {showColumnSelector && (
              <button className="dt-btn sm" onClick={() => setShowColSelector(!showColSelector)}>
                {t('poiColumns', 'Columns')}
              </button>
            )}
            <details className="dt-asset-menu"><summary className="dt-btn sm">{t('assetExport')}</summary><div className="dt-pop"><button className="dt-btn sm" onClick={() => handleExportCSV('matching')}>{t('assetExportResults')} ({displayedPOIs.length})</button><button className="dt-btn sm" disabled={!selectedPOIs.size} onClick={() => handleExportCSV('selected')}>{t('assetExportSelected')} ({selectedPOIs.size})</button></div></details>
            {/* Preserve HEAD's isAdmin gate in all variants; deletion still requires confirmation. */}
            {isAdmin && (
              <details className="dt-asset-menu"><summary className="dt-btn sm">{t('assetManage')}</summary><div className="dt-pop"><button
                className="dt-btn sm dngr"
                onClick={() => setShowDeleteAllModal(true)}
                title={t('poiDeleteAllTitle', 'Delete all assets in this site')}
              >
                {t('poiDeleteAll', 'Delete All')}
              </button></div></details>
            )}
            {showEditMode && canEditContent && (
              isEditMode ? (
                <>
                  <button className="dt-btn sm" onClick={handleCancelEditMode} disabled={savingPOIs}>Cancel</button>
                  <button
                    className="dt-btn pri sm"
                    onClick={handleSaveAllEdits}
                    disabled={savingPOIs || Object.keys(editedPOIs).length === 0}
                  >
                    Save all
                  </button>
                </>
              ) : (
                <button className="dt-btn pri sm" onClick={() => setIsEditMode(true)}>Edit table</button>
              )
            )}
            {renderExtraHeaderButtons?.()}
            {isSidebar && showExpandCollapsePanel && <button className="dt-btn sm" onClick={() => onPanelExpandedChange?.(!isPanelExpanded)}>{isPanelExpanded ? t('assetRestore') : t('assetExpand')}</button>}
            {onClose && <button className="dt-btn sm" onClick={onClose} aria-label={t('poiClosePanel', 'Close panel')}>×</button>}

            {/* Columns popover — anchored under the header actions */}
            {showColSelector && showColumnSelector && (() => {
              const standardCols = allAvailableColumns.filter(c => !c.field.startsWith('rvt:') && !c.field.startsWith('ifc:'));
              const rvtCols = allAvailableColumns.filter(c => c.field.startsWith('rvt:'));
              const ifcCols = allAvailableColumns.filter(c => c.field.startsWith('ifc:'));
              const searchLower = columnSearch.toLowerCase();
              const matches = (c: typeof allAvailableColumns[0]) => c.label.toLowerCase().includes(searchLower) || c.field.toLowerCase().includes(searchLower);
              const filteredStandard = standardCols.filter(matches);
              const filteredRvt = rvtCols.filter(matches);
              const filteredIfc = ifcCols.filter(matches);
              const renderColumnCheckbox = (col: typeof allAvailableColumns[0]) => (
                <label key={col.field} className="dt-cbrow" style={{ cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={columns.find(c => c.field === col.field)?.visible ?? col.visible}
                    onChange={(e) => {
                      const existing = columns.find(c => c.field === col.field);
                      if (existing) {
                        setColumns(columns.map(c => c.field === col.field ? { ...c, visible: e.target.checked } : c));
                      } else {
                        setColumns([...columns, { ...col, visible: e.target.checked }]);
                      }
                    }}
                  />
                  {col.label}
                </label>
              );
              return (
<AssetDialog label={t('poiColumns', 'Columns')} onClose={() => setShowColSelector(false)}>
                  <input
                    className="dt-field"
                    style={{ marginBottom: 8 }}
                    placeholder={t('poiSearchColumns', 'Search columns...')}
                    value={columnSearch}
                    onChange={(e) => setColumnSearch(e.target.value)}
                  />
                  {filteredStandard.length > 0 && (
                    <>
                      <div className="dt-grpttl" style={{ marginTop: 0 }}>Standard</div>
                      {filteredStandard.map(renderColumnCheckbox)}
                    </>
                  )}
                  {filteredRvt.length > 0 && (
                    <>
                      <div className="dt-grpttl">RVT properties ({filteredRvt.length})</div>
                      {filteredRvt.slice(0, 50).map(renderColumnCheckbox)}
                      {filteredRvt.length > 50 && (
                        <div style={{ fontSize: '.75rem', color: 'var(--dt-text-3)', padding: '2px 6px' }}>
                          ... and {filteredRvt.length - 50} more (use search to find)
                        </div>
                      )}
                    </>
                  )}
                  {filteredIfc.length > 0 && (
                    <>
                      <div className="dt-grpttl">IFC properties ({filteredIfc.length})</div>
                      {filteredIfc.slice(0, 50).map(renderColumnCheckbox)}
                      {filteredIfc.length > 50 && (
                        <div style={{ fontSize: '.75rem', color: 'var(--dt-text-3)', padding: '2px 6px' }}>
                          ... and {filteredIfc.length - 50} more (use search to find)
                        </div>
                      )}
                    </>
                  )}
                </AssetDialog>
              );
            })()}
          </div>
        </div>

        {availableRooms.length > 0 && assignmentStats.spaceAssigned < assignmentStats.total && <details className="dt-asset-coverage"><summary>{assignmentStats.total - assignmentStats.spaceAssigned} {t('assetWithoutRoom')}</summary><p>{t('poiRoomAssignmentWarning').replace('{count}', String(assignmentStats.total - assignmentStats.spaceAssigned)).replace('{total}', String(assignmentStats.total))}</p></details>}

        {/* ── Toolbar card ────────────────────────────────────────────────── */}
        <div className="dt-asset-controls">
          <div>
            {/* row 1: search + segmented toggle + filter controls + count */}
            <div className="dt-tbar">
              <input
                className="dt-field"
                style={{ width: 210, padding: '7px 10px' }}
                placeholder={t('poiSearchPlaceholder', 'Search...')}
                value={localSearchInput}
                onChange={(e) => {
                  const val = e.target.value;
                  setLocalSearchInput(val);
                  if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
                  searchTimerRef.current = setTimeout(() => {
                    if (val) trackEvent('poi.search', { oncePerSession: true });
                    setSearchTerm(val);
                    setCurrentPage(1);
                  }, 300);
                }}
              />
              {(isSidebar || viewerSelectedDbIds.length > 0 || filterMode === 'selected') && <div className="dt-seg">
                <button className={filterMode === 'all' ? 'on' : ''} onClick={() => { setFollowScene(false); setFilterMode('all'); setCurrentPage(1); }}>{t('assetAll')}</button>
                <button
                  className={filterMode === 'selected' ? 'on' : ''}
                  onClick={() => { setFilterMode('selected'); setCurrentPage(1); }} disabled={!sceneAssetSelection.length}
                  style={{ opacity: sceneAssetSelection.length === 0 ? 0.5 : 1 }}
                  title={sceneAssetSelection.length === 0 ? 'Select assets in the 3D viewer first' : `Show the ${sceneAssetSelection.length} selected assets`}
                >
                  {t('assetInScene')}{sceneAssetSelection.length > 0 ? ` · ${sceneAssetSelection.length}` : ''}
                </button>
              </div>}
              {(isSidebar || viewerSelectedDbIds.length > 0) && <label className="dt-asset-follow"><input type="checkbox" checked={followScene} onChange={event => setFollowScene(event.target.checked)} />{t('assetFollowScene')}</label>}
              <button className="dt-btn sm" onClick={openFilters}>{t('assetFind')}</button>
              {(Object.values(assetChoices).some(values => values.length) || appliedFilterGroups.length > 0) && <button className="dt-btn sm" onClick={() => { setAssetChoices({}); clearFilters(); }}>{t('assetClearFilters')}</button>}
              <span style={{ marginInlineStart: 'auto', fontSize: '.82rem', color: 'var(--dt-text-3)' }}>
                {displayedPOIs.length} assets · {selectedPOIs.size} selected
              </span>
            </div>

            {Object.entries(assetChoices).some(([, values]) => values.length) && <div className="dt-asset-filter-chips">{Object.entries(assetChoices).flatMap(([field, values]) => values.map(value => <button key={`${field}:${value}`} className="dt-btn sm" onClick={() => setAssetChoices(previous => ({ ...previous, [field]: previous[field].filter(v => v !== value) }))}>{t(`assetFacet_${field}`, fieldLabelFor(field))}: <Bdi>{value === UNASSIGNED ? t('assetNotAssigned') : value}</Bdi> ×</button>))}</div>}
            {/* row 2: active filter chips */}
            {activeConditions.length > 0 && (
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                <span style={{ fontSize: '.7rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.05em', color: 'var(--dt-text-3)' }}>Active</span>
                {activeConditions.map(({ groupId, cond }) => (
                  <span key={cond.id} className="dt-pill2" style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                    {fieldLabelFor(cond.field)} <b style={{ color: 'var(--dt-text)' }}>{operatorLabelFor(cond.operator)}</b>
                    {cond.operator !== 'isEmpty' && cond.operator !== 'isNotEmpty' && cond.value ? ` "${cond.value}"` : ''}
                    <button className="dt-btn sm" aria-label={t('assetRemoveFilter')} onClick={() => removeCondition(groupId, cond.id)}>×</button>
                  </span>
                ))}
              </div>
            )}

            {filterDialogOpen && <AssetFiltersDialog pois={sceneScopedPOIs} choices={assetChoices} read={facetReader} fields={allFilterFields}
              matchesRules={poi => matchesFilterGroups(poi, filterGroupDraft)}
              onClose={() => setFilterDialogOpen(false)}
              onApply={choices => { setAssetChoices(choices); setAppliedFilterGroups(filterGroupDraft); setCurrentPage(1); setFilterDialogOpen(false); }}
              advanced={<div style={{ borderTop: '1px solid var(--dt-border)', paddingTop: 11 }}>
                <div className="dt-grpttl" style={{ margin: '0 0 8px' }}>Filter builder</div>
                {filterGroups.map((group, groupIndex) => (
                  <div key={group.id} style={{ border: '1px solid var(--dt-border)', borderRadius: 9, padding: 10, background: 'var(--dt-surface-2)', marginBottom: 8 }}>
                    <div className="dt-facc" style={{ marginBottom: 8 }}>
                      <span style={{ fontWeight: 700, fontSize: '.8rem' }}>Group {groupIndex + 1}</span> · match
                      <select
                        className="dt-field"
                        style={{ width: 'auto', padding: '4px 8px' }}
                        value={group.logic}
                        onChange={(e) => updateGroupLogic(group.id, e.target.value as 'AND' | 'OR')}
                      >
                        <option value="AND">All (AND)</option>
                        <option value="OR">Any (OR)</option>
                      </select>
                      <button className="dt-btn sm dngr" style={{ marginInlineStart: 'auto' }} onClick={() => removeFilterGroup(group.id)}>
                        Remove group
                      </button>
                    </div>
                    {group.conditions.map(condition => {
                      const dropdownId = `${group.id}-${condition.id}`;
                      const isDropdownOpen = openFilterDropdown === dropdownId;
                      const searchLower = filterFieldSearch.toLowerCase();
                      const allFields = [
                        ...FILTER_FIELDS.map(f => ({ field: f.field, label: f.label, group: 'Standard' })),
                        ...dynamicColumns.filter(c => c.field.startsWith('rvt:')).map(c => ({ field: c.field, label: c.label, group: 'RVT' })),
                        ...dynamicColumns.filter(c => c.field.startsWith('ifc:')).map(c => ({ field: c.field, label: c.label, group: 'IFC' })),
                      ];
                      const filteredFields = isDropdownOpen && filterFieldSearch
                        ? allFields.filter(f => f.label.toLowerCase().includes(searchLower) || f.field.toLowerCase().includes(searchLower))
                        : allFields;
                      const currentField = allFields.find(f => f.field === condition.field);
                      const currentLabel = currentField?.label || condition.field;
                      const renderFieldOption = (f: { field: string; label: string; group: string }) => (
                        <div
                          key={f.field}
                          className="dt-cbrow"
                          onClick={() => {
                            updateCondition(group.id, condition.id, { field: f.field });
                            setOpenFilterDropdown(null);
                            setFilterFieldSearch('');
                          }}
                          style={{ cursor: 'pointer', color: condition.field === f.field ? 'var(--dt-accent-strong)' : undefined }}
                        >
                          {f.label}
                        </div>
                      );
                      return (
                        <div key={condition.id} style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 6, alignItems: 'center' }}>
                          <div style={{ position: 'relative', flex: 1, minWidth: 150 }}>
                            <button
                              type="button"
                              className="dt-field"
                              style={{ textAlign: 'start', cursor: 'pointer', position: 'relative', paddingInlineEnd: 24 }}
                              onClick={() => {
                                if (isDropdownOpen) { setOpenFilterDropdown(null); setFilterFieldSearch(''); }
                                else { setOpenFilterDropdown(dropdownId); setFilterFieldSearch(''); }
                              }}
                            >
                              {currentLabel}
                              <span style={{ position: 'absolute', insetInlineEnd: 8, top: '50%', transform: 'translateY(-50%)' }}>▼</span>
                            </button>
                            {isDropdownOpen && (
                              <div className="dt-pop" style={{ position: 'absolute', top: '100%', left: 0, right: 0, marginTop: 4, zIndex: 1001, maxHeight: 300, display: 'flex', flexDirection: 'column' }}>
                                <input
                                  className="dt-field"
                                  style={{ marginBottom: 6 }}
                                  placeholder={t('poiSearchFields', 'Search fields...')}
                                  value={filterFieldSearch}
                                  onChange={(e) => setFilterFieldSearch(e.target.value)}
                                  onClick={(e) => e.stopPropagation()}
                                  autoFocus
                                />
                                <div style={{ overflowY: 'auto', maxHeight: 250 }}>
                                  {filteredFields.filter(f => f.group === 'Standard').length > 0 && (
                                    <>
                                      <div className="dt-grpttl" style={{ marginTop: 0 }}>Standard</div>
                                      {filteredFields.filter(f => f.group === 'Standard').map(renderFieldOption)}
                                    </>
                                  )}
                                  {filteredFields.filter(f => f.group === 'RVT').length > 0 && (
                                    <>
                                      <div className="dt-grpttl">RVT properties ({filteredFields.filter(f => f.group === 'RVT').length})</div>
                                      {filteredFields.filter(f => f.group === 'RVT').slice(0, 50).map(renderFieldOption)}
                                    </>
                                  )}
                                  {filteredFields.filter(f => f.group === 'IFC').length > 0 && (
                                    <>
                                      <div className="dt-grpttl">IFC properties ({filteredFields.filter(f => f.group === 'IFC').length})</div>
                                      {filteredFields.filter(f => f.group === 'IFC').slice(0, 50).map(renderFieldOption)}
                                    </>
                                  )}
                                  {filteredFields.length === 0 && (
                                    <div style={{ padding: 8, fontSize: '.8rem', color: 'var(--dt-text-3)', textAlign: 'center' }}>No fields found</div>
                                  )}
                                </div>
                              </div>
                            )}
                          </div>
                          <select
                            className="dt-field"
                            style={{ width: 'auto', padding: '6px 8px' }}
                            value={condition.operator}
                            onChange={(e) => updateCondition(group.id, condition.id, { operator: e.target.value as FilterCondition['operator'] })}
                          >
                            {OPERATORS.map(op => <option key={op.value} value={op.value}>{op.label}</option>)}
                          </select>
                          {condition.operator !== 'isEmpty' && condition.operator !== 'isNotEmpty' && (
                            <input
                              className="dt-field"
                              style={{ width: 130, padding: '6px 10px' }}
                              value={condition.value}
                              onChange={(e) => updateCondition(group.id, condition.id, { value: e.target.value })}
                              placeholder={t('poiValuePlaceholder', 'Value...')}
                            />
                          )}
                          <button className="dt-btn sm" onClick={() => removeCondition(group.id, condition.id)}>×</button>
                        </div>
                      );
                    })}
                    <button className="dt-btn sm" onClick={() => addCondition(group.id)}>+ Add condition</button>
                  </div>
                ))}
                <button className="dt-btn sm" onClick={addFilterGroup}>+ Add group</button>
              </div>} />}

            {/* row 4: bulk actions (only when a selection exists) */}
            {selectedPOIs.size > 0 && (
              <div className="dt-tbar dt-asset-selection" style={{ borderTop: '1px solid var(--dt-border)', paddingTop: 11 }}>
                <span style={{ fontSize: '.82rem', color: 'var(--dt-text-2)', fontWeight: 600 }}>{selectedPOIs.size} selected</span>
                {(onHighlightGroup || onBulkSelectionChange) && (
                  <button className="dt-btn sm" onClick={showSelectedIn3D}>Show in 3D</button>
                )}
                {showBulkActions && canEditContent && (
                  <>
                    <button className="dt-btn sm" onClick={handleOpenSetSystemsModal}>Set systems</button>
                    <button className="dt-btn sm" onClick={handleOpenSetLocationsModal}>Set locations</button>
                    <button className="dt-btn sm dngr" onClick={() => setShowDeleteSelectedModal(true)} title={t('poiDeleteSelectedTitle', 'Delete selected assets')}>Delete</button>
                  </>
                )}
                {displayedPOIs.some(poi => !selectedPOIs.has(String(poi.id))) && <button className="dt-btn sm" onClick={() => setSelectedPOIs(new Set([...selectedPOIs, ...displayedPOIs.map(poi => String(poi.id))]))}>{t('assetSelectResults')} ({displayedPOIs.length})</button>}
                {selectedOutsideResults > 0 && <span className="dt-asset-muted">{selectedOutsideResults} {t('assetOutsideResults')}</span>}
                <button className="dt-btn sm" onClick={() => setSelectedPOIs(new Set())}>{t('assetClearSelection')}</button>
                {renderExtraActions?.(Array.from(selectedPOIs).map(id => pois.find(p => String(p.id) === id)).filter(Boolean))}
              </div>
            )}
          </div>
        </div>

        {/* ── Selected in 3D, not an asset yet ─────────────────────────────
            The agreed rule (audit 2026-10-05, bug 6): the list shrinks to the selected ASSETS only;
            each selected element that is not one is named here — "Floor [481276]: not an asset
            yet" — with the Element panel's Create asset for people who may create assets. A new
            asset stays selected, so it moves from this line into the list. */}
        {followScene && sceneNonAssets.length > 0 && (
          <div className="dt-asset-scene-line" role="status" aria-live="polite">
            {(sceneLineExpanded ? sceneNonAssets : sceneNonAssets.slice(0, SCENE_LINE_MAX)).map(element => {
              const key = `${element.sourceFile || ''}:${element.dbId}`;
              return (
                <span key={key} className="dt-asset-scene-item">
                  {/* Only a long NAME is shortened; "not an asset yet" always reads in full. */}
                  <span className="dt-asset-scene-name" title={element.label || undefined}><Bdi>{element.label || `${t('assetSceneElement', 'Element')} ${element.dbId}`}</Bdi></span>
                  <span className="dt-asset-scene-state">: {t('assetNotAssetYet', 'not an asset yet')}</span>
                  {canCreateSceneAsset && (
                    <button
                      className="dt-btn sm"
                      disabled={creatingSceneKey !== null}
                      onClick={async () => {
                        setCreatingSceneKey(key);
                        try { await onCreateAssetForSceneElement?.({ dbId: element.dbId, sourceFile: element.sourceFile ?? null }); }
                        finally { setCreatingSceneKey(null); }
                      }}
                    >
                      {creatingSceneKey === key ? t('assetCreating', 'Creating…') : t('dt_element_action_createAsset', 'Create asset')}
                    </button>
                  )}
                </span>
              );
            })}
            {sceneNonAssets.length > SCENE_LINE_MAX && (
              <button className="dt-btn sm" onClick={() => setSceneLineExpanded(expanded => !expanded)}>
                {sceneLineExpanded ? t('assetSceneFewer', 'Show fewer') : t('assetSceneMore', '+{count} more').replace('{count}', String(sceneNonAssets.length - SCENE_LINE_MAX))}
              </button>
            )}
          </div>
        )}

        {/* ── Table card ──────────────────────────────────────────────────── */}
        <div className="dt-asset-table-card">
          <div className="dt-asset-table-scroll">
            <table ref={tableRef} className="dt-tbl" style={{ width: totalTableWidth, tableLayout: 'fixed' }}>
              <thead>
                <tr>
                  <th style={{ width: 22 }}></th>
                  <th style={{ width: 30 }}>
                    <input
                      type="checkbox"
                      aria-label={t('assetSelectPage')}
                      checked={localPaginatedPOIs.length > 0 && localPaginatedPOIs.every(poi => selectedPOIs.has(String(poi.id)))}
                      ref={input => { if (input) input.indeterminate = localPaginatedPOIs.some(poi => selectedPOIs.has(String(poi.id))) && !localPaginatedPOIs.every(poi => selectedPOIs.has(String(poi.id))); }}
                      onChange={() => handleSelectAll(localPaginatedPOIs)}
                    />
                  </th>
                  {visibleColumns.map(col => (
                    <th
                      key={col.field}
                      style={{
                        width: col.width, position: 'relative', cursor: 'pointer',
                        color: sortConfig.field === col.field ? 'var(--dt-accent-strong)' : undefined,
                        whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                      }}
                      onClick={() => { if (wasResizingRef.current) return; handleSort(col.field); }}
                    >
                      {col.label}
                      {sortConfig.field === col.field && (
                        <span style={{ marginInlineStart: 4 }}>{sortConfig.direction === 'asc' ? '▲' : '▼'}</span>
                      )}
                      <div
                        style={{ position: 'absolute', insetInlineEnd: 0, top: 0, bottom: 0, width: 8, cursor: 'col-resize', backgroundColor: 'transparent', zIndex: 1 }}
                        onMouseDown={(e) => handleResizeStart(e, col.field, col.width)}
                        onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = 'var(--dt-accent-tint)')}
                        onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
                      />
                    </th>
                  ))}
                  <th style={{ width: ACTIONS_COL_WIDTH, textAlign: 'end' }}>{t('poiActions', 'Actions')}</th>
                </tr>
              </thead>
              <tbody>
                {/* Reset search resets the search: it no longer also turns "Follow scene selection" off
                    behind the person's back (audit 2026-10-05, bug 6). While the list reloads after a
                    change made in the viewer, an empty result is "loading", not "no match". */}
                {!localPaginatedPOIs.length && (hostReloading && filterMode === 'selected'
                  ? <tr><td colSpan={visibleColumns.length + 3}><div className="dt-statebox">{t('loading', 'Loading assets...')}</div></td></tr>
                  : <tr><td colSpan={visibleColumns.length + 3}><div className="dt-statebox">{pois.length ? t('assetNoResults') : t('assetNoAssets')}<button className="dt-btn sm" onClick={() => { setSearchTerm(''); setAssetChoices({}); clearFilters(); setFilterMode('all'); }}>{t('assetResetSearch')}</button></div></td></tr>)}
                {localPaginatedPOIs.map(poi => {
                  const poiIdStr = String(poi.id);
                  const isSelected = selectedPOIs.has(poiIdStr);
                  const isExpanded = expandedPOI === poiIdStr;
                  const poiSourceFile = getPOISourceFile(poi);
                  const isLoaded = isPOILoaded(poi, loadedModels);
                  const isModelLoading = isPOILoading(poi, loadingModels);
                  const elementDbId = getPOIElementDbId(poi);

                  return (
                    <React.Fragment key={poi.id}>
                      {/* ONE CLICK SHOWS IT, THE CHEVRON EXPLAINS IT (product decision, 2026-09-22).
                          A click used to open a 430px inspector OVER the right of the table —
                          and in the viewer that was all it did: nothing moved in the model.
                          Now a click marks the row active and, where a model is on screen,
                          flies to the element (onPOIClick); the details open in place, under
                          the line, from the chevron or a double-click — as the table did
                          before the floating card replaced it. */}
                      <tr
                        className={activePOI === poiIdStr ? 'is-active' : undefined}
                        style={{ background: isExpanded ? 'var(--dt-surface-2)' : isSelected ? 'var(--dt-accent-tint)' : undefined, cursor: 'pointer' }}
                        onClick={() => { setActivePOI(poiIdStr); onPOIClick?.(poi); }}
                        onDoubleClick={() => setExpandedPOI(isExpanded ? null : poiIdStr)}
                      >
                        <td><button className="dt-asset-inspect-btn" aria-label={`${t('assetDetails')}: ${poi.title || poi.name}`} aria-expanded={isExpanded} onClick={event => { event.stopPropagation(); setExpandedPOI(isExpanded ? null : poiIdStr); }}>{isExpanded ? '▼' : '▶'}</button></td>
                        <td>
                          <input
                            type="checkbox"
                            checked={isSelected}
                            aria-label={`${t('assetSelect')}: ${poi.title || poi.name}`}
                            onChange={() => handleSelectPOI(poiIdStr)}
                            onClick={(e) => e.stopPropagation()}
                          />
                        </td>
                        {visibleColumns.map(col => {
                          const originalValue = getFieldValue(poi, col.field);
                          const editValue = editedPOIs[poiIdStr]?.[col.field] ?? originalValue;
                          const isEditable = isEditMode && roomEditableFields.includes(col.field);
                          const isRoomField = isEditMode && col.field === 'room_name';

                          if (isEditable) {
                            return (
                              <td key={col.field} style={{ overflow: 'hidden' }}>
                                <input
                                  className="dt-field"
                                  style={{ padding: '4px 8px', fontSize: '.82rem' }}
                                  value={editValue}
                                  onChange={(e) => handleFieldChange(poiIdStr, col.field, e.target.value, originalValue)}
                                  onClick={(e) => e.stopPropagation()}
                                />
                              </td>
                            );
                          }

                          if (isRoomField) {
                            const roomValue = editedPOIs[poiIdStr]?.room_name ?? originalValue;
                            const isRoomPickerOpen = roomPickerOpenForPOI === poi.id;
                            return (
                              <td key={col.field} style={{ position: 'relative', overflow: 'visible', zIndex: isRoomPickerOpen ? 1002 : 1 }}>
                                <button type="button"
                                  className="dt-field"
                                  onClick={(e) => { e.stopPropagation(); setRoomPickerOpenForPOI(isRoomPickerOpen ? null : poi.id); }}
                                  style={{ cursor: 'pointer', minHeight: 24, padding: '5px 8px' }}
                                >
                                  {roomValue || <span style={{ color: 'var(--dt-text-3)' }}>Select rooms...</span>}
                                </button>
                                {isRoomPickerOpen && (
                                  <AssetDialog label={t('assetChooseRooms')} onClose={() => setRoomPickerOpenForPOI(null)}>
                                    {availableRooms.length === 0 && (
                                      <div style={{ padding: '8px 10px', fontSize: '.8rem', color: 'var(--dt-text-3)' }}>No rooms available</div>
                                    )}
                                    {availableRooms.map(room => {
                                      const currentRooms = editedPOIs[poiIdStr]?.room_name_array || assignedRoomNames(poi);
                                      const isChecked = currentRooms.includes(room.name);
                                      return (
                                        <label key={room.id} className="dt-cbrow" style={{ cursor: 'pointer' }} onClick={(e) => e.stopPropagation()}>
                                          <input type="checkbox" checked={isChecked} onChange={() => handleRoomToggle(poi.id, room.name)} />
                                          <span>
                                            {room.name}
                                            {room.level_name && <span style={{ color: 'var(--dt-text-3)' }}> · {room.level_name}</span>}
                                          </span>
                                        </label>
                                      );
                                    })}
                                  </AssetDialog>
                                )}
                              </td>
                            );
                          }

                          if (col.field === 'system_category' && originalValue) {
                            return (
                              <td key={col.field} style={{ overflow: 'hidden' }}>
                                <span className="dt-chip" style={{ background: 'var(--dt-surface-3)', color: 'var(--dt-text-2)' }}>
                                  <span className="cd" style={{ background: systemChipColor(String(originalValue)) }} />
                                  {originalValue}
                                </span>
                              </td>
                            );
                          }

                          if (col.field === 'name') {
                            return (
                              <td key={col.field} style={{ overflow: 'hidden' }}>
                                <b style={{ color: 'var(--dt-text)' }}>{originalValue}</b>
                                {typeof elementDbId === 'number' && <div className="dt-mono">{elementDbId}</div>}
                              </td>
                            );
                          }

                          if (col.field === 'room_name' && !originalValue) {
                            return (
                              <td key={col.field} style={{ overflow: 'hidden' }}>
                                <span style={{ color: 'var(--dt-text-3)' }}>— </span>
                                <span className="dt-pill2" style={{ color: 'var(--dt-warn)', background: 'var(--dt-warn-t)' }}>unassigned</span>
                              </td>
                            );
                          }

                          return (
                            <td key={col.field} style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{originalValue}</td>
                          );
                        })}
                        <td style={{ textAlign: 'end', whiteSpace: 'nowrap' }}>
                          <span style={{ display: 'inline-flex', gap: 5, justifyContent: 'flex-end' }}>
                            {onNavigateTo3D && (isSidebar || isLoaded) && (
                              <button className="dt-btn sm" onClick={(e) => { e.stopPropagation(); onNavigateTo3D(poi); }}>3D</button>
                            )}
                            {onHighlightGroup && poi.system_category && (isSidebar || isLoaded) && (
                              <button
                                className="dt-btn sm"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  const systemPOIs = pois.filter(p => p.system_category === poi.system_category);
                                  logger.debug('[UnifiedPOITable] System button: highlighting', systemPOIs.length, 'POIs for system:', poi.system_category);
                                  onHighlightGroup(systemPOIs, '#48CAE4');
                                }}
                                title={`Show all ${poi.system_category} elements in 3D`}
                              >
                                System
                              </button>
                            )}
                            {onRequestLoadModel && poiSourceFile && !isLoaded && !isModelLoading && (
                              <button className="dt-btn sm" onClick={(e) => { e.stopPropagation(); onRequestLoadModel(poiSourceFile); }}>Load</button>
                            )}
                            {isModelLoading && (
                              <span style={{ fontSize: '.75rem', color: 'var(--dt-text-3)' }}>Loading...</span>
                            )}
                          </span>
                        </td>
                      </tr>
                      {isExpanded && (
                        <tr className="dt-asset-expanded">
                          <td colSpan={visibleColumns.length + 3}>
                            <div className="dt-asset-details"><div className="dt-asset-connections">
              {onNavigateTo3D && (isSidebar || isPOILoaded(poi, loadedModels)) && <button className="dt-btn sm" onClick={() => onNavigateTo3D(poi)}>{t('assetLocate')}</button>}
              {onRequestLoadModel && getPOISourceFile(poi) && !isPOILoaded(poi, loadedModels) && <button className="dt-btn sm" disabled={isPOILoading(poi, loadingModels)} onClick={() => onRequestLoadModel(getPOISourceFile(poi)!)}>{t('assetLoadModel')}</button>}
              </div><div className="dt-propgrid">{['room_name', 'level', 'system_category', 'source_file'].map(field => <React.Fragment key={field}><span className="k">{t(`assetFacet_${field}`)}</span><span className="v"><Bdi>{facetReader(poi, field).map(value => value === UNASSIGNED ? t('assetNotAssigned') : value).join('; ')}</Bdi></span></React.Fragment>)}</div><AssetConnections key={poi.id} siteId={siteId} poi={poi} canEdit={canEditContent} onViewTickets={onViewTickets} onCreateTicket={onCreateTicket} onChanged={handlePOIMutationSuccess} /><POIAttributesPanel poi={poi} colors={colors} dt /></div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* pagination (inside the table card) */}
          {(
            <div className="dt-asset-footer">
              <button className="dt-btn sm" onClick={() => setCurrentPage(Math.max(1, displayPage - 1))} disabled={displayPage === 1}>Prev</button>
              <span>{t('assetPage')} {displayPage} / {localTotalPages}</span>
              <button className="dt-btn sm" onClick={() => setCurrentPage(Math.min(localTotalPages, displayPage + 1))} disabled={displayPage === localTotalPages}>Next</button>
              <select
                className="dt-field"
                style={{ width: 'auto', marginInlineStart: 'auto', padding: '6px 8px' }}
                value={pageSize}
                onChange={(e) => { setPageSize(Number(e.target.value)); setCurrentPage(1); }}
              >
                {PAGE_SIZE_OPTIONS.map(size => <option key={size} value={size}>{size} / page</option>)}
              </select>
            </div>
          )}
        </div>

        {/* ── Delete All modal ────────────────────────────────────────────── */}
        {showDeleteAllModal && (
          <AssetDialog label={t('poiDeleteAllConfirmTitle')} onClose={() => setShowDeleteAllModal(false)}>
            <div className="dt-card" style={{ padding: 20, maxWidth: 420, width: '90%' }}>
              <div style={{ fontWeight: 700, fontSize: 16, color: 'var(--dt-bad)', marginBottom: 10 }}>{t('poiDeleteAllConfirmTitle', 'Delete All Assets?')}</div>
              <div style={{ fontSize: '.86rem', color: 'var(--dt-text-2)', marginBottom: 14 }}>
                This will permanently delete all {pois.length} assets from this site. This action cannot be undone.
              </div>
              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                <button className="dt-btn sm" onClick={() => setShowDeleteAllModal(false)} disabled={isDeletingAll}>Cancel</button>
                <button className="dt-btn sm dngr" onClick={handleDeleteAll} disabled={isDeletingAll}>
                  {isDeletingAll ? t('poiDeleting', 'Deleting...') : t('poiDeleteAll', 'Delete All')}
                </button>
              </div>
            </div>
          </AssetDialog>
        )}

        {/* ── Delete Selected modal ───────────────────────────────────────── */}
        {showDeleteSelectedModal && (
          <AssetDialog label={t('poiDeleteSelectedConfirmTitle')} onClose={() => setShowDeleteSelectedModal(false)}>
            <div className="dt-card" style={{ padding: 20, maxWidth: 420, width: '90%' }}>
              <div style={{ fontWeight: 700, fontSize: 16, color: 'var(--dt-bad)', marginBottom: 10 }}>{t('poiDeleteSelectedConfirmTitle', 'Delete Selected Assets?')}</div>
              <div style={{ fontSize: '.86rem', color: 'var(--dt-text-2)', marginBottom: 14 }}>
                This will permanently delete {selectedPOIs.size} selected asset{selectedPOIs.size !== 1 ? 's' : ''} from this site. This action cannot be undone.
              </div>
              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                <button className="dt-btn sm" onClick={() => setShowDeleteSelectedModal(false)} disabled={isDeletingSelected}>Cancel</button>
                <button className="dt-btn sm dngr" onClick={handleDeleteSelected} disabled={isDeletingSelected}>
                  {isDeletingSelected ? t('poiDeleting', 'Deleting...') : `${t('delete', 'Delete')} ${selectedPOIs.size}`}
                </button>
              </div>
            </div>
          </AssetDialog>
        )}

        {/* ── Set Systems modal ───────────────────────────────────────────── */}
        {showSetSystemsModal && (
          <AssetDialog label={t('poiSetSystems')} onClose={() => setShowSetSystemsModal(false)}>
            <div className="dt-card" style={{ padding: 20, minWidth: 400, maxWidth: 500, width: '90%', maxHeight: '80vh', overflowY: 'auto' }}>
              <div style={{ fontWeight: 700, fontSize: 16, color: 'var(--dt-text)', marginBottom: 12 }}>{t('poiSetSystems', 'Set Systems')}</div>
              {isLoadingSystemsProperties ? (
                <div className="dt-muted" style={{ textAlign: 'center', padding: 20 }}>Loading...</div>
              ) : (
                <>
                  <label className="dt-flabel">System Property</label>
                  <input
                    className="dt-field"
                    style={{ marginBottom: 6 }}
                    placeholder={t('poiSearchPlaceholder', 'Search...')}
                    value={systemPropertySearch}
                    onChange={(e) => setSystemPropertySearch(e.target.value)}
                  />
                  <div className="dt-pop" style={{ maxHeight: 150, overflowY: 'auto', marginBottom: 12 }}>
                    {systemsAvailableProperties.filter(p => p.toLowerCase().includes(systemPropertySearch.toLowerCase())).slice(0, 30).map(prop => (
                      <div
                        key={prop}
                        className="dt-cbrow"
                        onClick={() => setSelectedSystemProperty(prop)}
                        style={{ cursor: 'pointer', background: selectedSystemProperty === prop ? 'var(--dt-accent-tint)' : undefined }}
                      >
                        <input type="radio" name="dt-sys-prop" checked={selectedSystemProperty === prop} readOnly />
                        {prop}
                        <span style={{ marginInlineStart: 'auto', color: 'var(--dt-text-3)' }}>({systemsPropertyValues[prop]?.length || 0})</span>
                      </div>
                    ))}
                  </div>
                  {systemsResult && (
                    <div className="dt-facc" style={{ marginBottom: 12, fontSize: '.82rem' }}>
                      Result: <span style={{ color: 'var(--dt-good)' }}>Updated {systemsResult.updated}</span> · Unchanged {systemsResult.unchanged} · Failed {systemsResult.failed}
                    </div>
                  )}
                </>
              )}
              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                <button className="dt-btn sm" onClick={() => { setShowSetSystemsModal(false); setSystemsResult(null); }}>
                  {systemsResult ? t('close', 'Close') : t('cancel', 'Cancel')}
                </button>
                {!systemsResult && (
                  <button className="dt-btn pri sm" onClick={handleApplySystemsBatch} disabled={isApplyingSystems || !selectedSystemProperty}>
                    {isApplyingSystems ? t('poiApplying', 'Applying...') : t('poiApply', 'Apply')}
                  </button>
                )}
              </div>
            </div>
          </AssetDialog>
        )}

        {/* ── Set Locations modal ─────────────────────────────────────────── */}
        {showSetLocationsModal && (
          <AssetDialog label={t('poiSetLocations')} onClose={() => setShowSetLocationsModal(false)}>
            <div className="dt-card" style={{ padding: 20, minWidth: 400, maxWidth: 500, width: '90%', maxHeight: '80vh', overflowY: 'auto' }}>
              <div style={{ fontWeight: 700, fontSize: 16, color: 'var(--dt-text)', marginBottom: 12 }}>{t('poiSetLocations', 'Set Locations')}</div>
              {isLoadingLocationsProperties ? (
                <div className="dt-muted" style={{ textAlign: 'center', padding: 20 }}>Loading...</div>
              ) : (
                <>
                  <label className="dt-flabel">Storey property</label>
                  <input
                    className="dt-field"
                    style={{ marginBottom: 6 }}
                    placeholder={t('poiSearchPlaceholder', 'Search...')}
                    value={storeyPropertySearch}
                    onChange={(e) => setStoreyPropertySearch(e.target.value)}
                  />
                  <div className="dt-pop" style={{ maxHeight: 120, overflowY: 'auto', marginBottom: 12 }}>
                    <div
                      className="dt-cbrow"
                      onClick={() => setSelectedStoreyProperty('')}
                      style={{ cursor: 'pointer', fontStyle: 'italic', color: 'var(--dt-text-3)', background: selectedStoreyProperty === '' ? 'var(--dt-accent-tint)' : undefined }}
                    >
                      -- None --
                    </div>
                    {locationsAvailableProperties.filter(p => p.toLowerCase().includes(storeyPropertySearch.toLowerCase())).slice(0, 30).map(prop => (
                      <div
                        key={prop}
                        className="dt-cbrow"
                        onClick={() => setSelectedStoreyProperty(prop)}
                        style={{ cursor: 'pointer', background: selectedStoreyProperty === prop ? 'var(--dt-accent-tint)' : undefined }}
                      >
                        {prop}
                      </div>
                    ))}
                  </div>
                  <label className="dt-flabel">Space / room property</label>
                  <input
                    className="dt-field"
                    style={{ marginBottom: 6 }}
                    placeholder={t('poiSearchPlaceholder', 'Search...')}
                    value={spacePropertySearch}
                    onChange={(e) => setSpacePropertySearch(e.target.value)}
                  />
                  <div className="dt-pop" style={{ maxHeight: 120, overflowY: 'auto', marginBottom: 12 }}>
                    <div
                      className="dt-cbrow"
                      onClick={() => setSelectedSpaceProperty('')}
                      style={{ cursor: 'pointer', fontStyle: 'italic', color: 'var(--dt-text-3)', background: selectedSpaceProperty === '' ? 'var(--dt-accent-tint)' : undefined }}
                    >
                      -- None --
                    </div>
                    {locationsAvailableProperties.filter(p => p.toLowerCase().includes(spacePropertySearch.toLowerCase())).slice(0, 30).map(prop => (
                      <div
                        key={prop}
                        className="dt-cbrow"
                        onClick={() => setSelectedSpaceProperty(prop)}
                        style={{ cursor: 'pointer', background: selectedSpaceProperty === prop ? 'var(--dt-accent-tint)' : undefined }}
                      >
                        {prop}
                      </div>
                    ))}
                  </div>
                  {locationsResult && (
                    <div className="dt-facc" style={{ marginBottom: 12, fontSize: '.82rem' }}>
                      Result: <span style={{ color: 'var(--dt-good)' }}>Updated {locationsResult.updated}</span> · Unchanged {locationsResult.unchanged} · Failed {locationsResult.failed}
                    </div>
                  )}
                </>
              )}
              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                <button className="dt-btn sm" onClick={() => { setShowSetLocationsModal(false); setLocationsResult(null); }}>
                  {locationsResult ? t('close', 'Close') : t('cancel', 'Cancel')}
                </button>
                {!locationsResult && (
                  <button className="dt-btn pri sm" onClick={handleApplyLocationsBatch} disabled={isApplyingLocations || (!selectedStoreyProperty && !selectedSpaceProperty)}>
                    {isApplyingLocations ? t('poiApplying', 'Applying...') : t('poiApply', 'Apply')}
                  </button>
                )}
              </div>
            </div>
          </AssetDialog>
        )}
      </div>
    );
  }

};

export default UnifiedPOITable;
